import React, { memo, useMemo } from "react";
import { Box, Stack, Text } from "@chakra-ui/react";
import { MessageFragment, MessageThreadFragment } from "gql/graphql";
import colors from "app/colors";
import { getEmojiCount, isOnlyEmojis } from "modules/utils/Functions";
import useMessageThreadTranslation from "modules/hooks/use-message-thread-translation";
import MessagePlainText from "./message-plain-text";
import useQueryString from "modules/hooks/use-query-string";
import useMessageText from "modules/hooks/use-message-text";
import useQuillViewer from "modules/quill/use-quill-viewer";
import { quillDeltaToHtml } from "modules/quill/quill-delta-to-html";
import QuillClickedMention from "modules/quill/mentions/quill-clicked-mention";
import { SystemStyleObject } from "@chakra-ui/system";
import DOMPurify from "dompurify";

interface IProps {
  messageThread?: MessageThreadFragment;
  message: MessageFragment;
  displayEdited?: boolean;
  maxLines?: number;
  style?: SystemStyleObject;
}

const MessageText = ({
  messageThread,
  message,
  displayEdited = true,
  maxLines,
  style = {
    fontFamily: "var(--font-inter) !important",
    fontSize: "14px !important",
  },
}: IProps) => {
  const { isViewingTranslatedMessage } = useMessageThreadTranslation(
    message,
    messageThread,
  );
  const { qsMessageHighlights: searchTerms } = useQueryString();
  const { messageHasContent } = useMessageText(message, messageThread);

  const { containerRef, clickedMention, closeClickedMention, isReady } =
    useQuillViewer({
      delta: message.quillDelta,
      searchTerms,
    });

  // static stand-in rendered until Quill hydrates, so the message mounts at
  // (nearly) its final height instead of growing from empty and displacing
  // the list's scroll position
  const placeholderHtml = useMemo(
    () => DOMPurify.sanitize(quillDeltaToHtml(message.quillDelta)),
    [message.quillDelta],
  );

  const isEmoji = useMemo(() => {
    if (!message.text) return false;
    const emojiCount = getEmojiCount(message.text);
    return emojiCount > 0 && emojiCount < 3 && isOnlyEmojis(message.text);
  }, [message.text]);

  const isEdited = message.editTime !== null;

  const textStyle: SystemStyleObject = useMemo(() => {
    const _style = { ...style };
    if (maxLines) {
      _style.display = "-webkit-box";
      _style.WebkitLineClamp = maxLines;
      _style.lineClamp = maxLines;
      _style.WebkitBoxOrient = "vertical";
      _style.overflow = "hidden";
    }
    return _style;
  }, [style, maxLines]);

  if (!messageHasContent) {
    return null;
  }

  if (isEmoji) {
    return (
      <Stack spacing={0}>
        <Text fontSize={50}>{message.text}</Text>
        {displayEdited && <Edited isEdited={isEdited} />}
      </Stack>
    );
  }

  if (isViewingTranslatedMessage) {
    return (
      <Stack spacing={0}>
        <MessagePlainText
          text={message.localizedText}
          highlightText={searchTerms}
        />
        {displayEdited && <Edited isEdited={isEdited} />}
      </Stack>
    );
  }

  if (message.quillDelta) {
    return (
      <Stack spacing={0}>
        {!isReady && (
          <Box
            sx={{
              ...textStyle,
              // match quill's .ql-editor defaults so the placeholder height
              // is as close as possible to the hydrated editor
              lineHeight: 1.42,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              overflowWrap: "break-word",
              "& p": { margin: 0 },
              "& ol, & ul": { paddingLeft: "0.5em" },
            }}
            dangerouslySetInnerHTML={{ __html: placeholderHtml }}
          />
        )}
        <Box
          id="editor"
          ref={containerRef}
          display={isReady ? "block" : "none"}
          sx={{
            ".ql-editor": {
              ...textStyle,
            },
          }}
        />
        <QuillClickedMention
          clickedMention={clickedMention}
          onClose={closeClickedMention}
        />
        {displayEdited && <Edited isEdited={isEdited} />}
      </Stack>
    );
  }

  return (
    <Stack spacing={0}>
      <MessagePlainText text={message.text} highlightText={searchTerms} />
      {displayEdited && <Edited isEdited={isEdited} />}
    </Stack>
  );
};

const Edited = ({ isEdited }: { isEdited: boolean }) => {
  if (!isEdited) return null;

  return (
    <Box
      fontStyle="italic"
      fontWeight={400}
      color={colors.gray30}
      fontSize={12}
    >
      (Edited)
    </Box>
  );
};

export default memo(MessageText);
